
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h1 className="text-5xl font-bold text-[#2C2C2C] mb-6" style={{ fontFamily: 'Literata, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-12">Last updated: {today}</p>

          <div className="space-y-10 text-[#2C2C2C]" style={{ fontFamily: 'Literata, serif' }}>
            <section>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Privacy Policy explains how the I Say I Am app ("the App", "we", "our", "us") collects, uses, and protects your information. We prioritize user privacy and keep data collection to a minimum.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We only collect the information necessary to provide core features of the App.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Personal Data</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We do not require you to create an account.</li>
                <li>We do not collect your name, email, or any personal identifiers through the App.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Usage Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect anonymous usage data, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Language preference</li>
                <li>App version</li>
                <li>Device type (iOS/Android model)</li>
                <li>Non-identifiable analytics (e.g., screens visited, feature usage)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                This helps improve app performance and user experience.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Journals, Reflections, Affirmations</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Any text you write inside the App (affirmations, gratitude entries, reflections, notes) is stored locally on your device.</li>
                <li>We do not upload your personal content to our servers.</li>
                <li>If you enable cloud sync (optional), entries may be stored in your private iCloud Drive or Google Drive, depending on your device. We never access these files.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Audio & Images</h3>
              <p className="text-gray-700 leading-relaxed">
                The App loads audio and image assets from Firebase Storage. Firebase may collect basic technical logs (e.g., IP address, download timestamps) for security and performance.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold mb-4">Cookies</h3>
              <p className="text-gray-700 leading-relaxed">
                We do not use cookies inside the App.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Improve app stability</li>
                <li>Fix crashes and bugs</li>
                <li>Understand which features users find helpful</li>
                <li>Deliver content (affirmations, audio, images) from cloud storage</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>We never sell or share your information.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The App may use:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Firebase Storage for hosting media assets</li>
                <li>Firebase Analytics (optional)</li>
                <li>Apple iCloud / Google Drive for user backups</li>
                <li>App Store and Play Store billing for subscriptions or purchases</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Each service has its own privacy policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We take reasonable technical measures to protect your information. No method of storage or transmission is 100% secure, but we commit to minimizing risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Your Rights (GDPR & CCPA)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access your data</li>
                <li>Delete your data</li>
                <li>Update or correct your data</li>
                <li>Withdraw consent for analytics (if enabled)</li>
                <li>Request clarification at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To contact us: <a href="mailto:hello@thinkheart.co" className="text-[#FF6B6B] hover:underline cursor-pointer">hello@thinkheart.co</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                The App is not directed at children under 13 and does not knowingly collect personal information from minors.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Changes will be posted within the App and on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Contact</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions: <a href="mailto:hello@thinkheart.co" className="text-[#FF6B6B] hover:underline cursor-pointer">hello@thinkheart.co</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
